import { useState, useEffect } from 'react';
import { API_URL } from '../config';

/**
 * Hook personalizado para gestionar la configuración de la aplicación
 * Proporciona funciones para obtener y actualizar configuración
 */
const useSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para obtener configuración de contacto (pública)
  const fetchContactInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/settings/contact`);

      if (response.ok) {
        const result = await response.json();

        if (result.success && result.data) {
          setSettings(result.data);
          return result.data;
        } else {
          throw new Error('No se pudo obtener la configuración');
        }
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error al obtener configuración de contacto:', err);
      setError(err.message);

      // Devolver configuración por defecto en caso de error
      const defaultSettings = {
        contact: {
          address: {
            street: 'Calle Principal 123',
            city: 'Madrid',
            postalCode: '28001',
            country: 'España'
          },
          phone: '686254944',
          email: 'contactoligaprokids@gmail.com',
          schedule: {
            weekdays: 'Lunes a Viernes: 9:00 - 18:00',
            weekends: 'Sábados: 10:00 - 14:00',
            holidays: 'Domingos y festivos: Cerrado'
          }
        },
        socialMedia: {
          facebook: {
            url: 'https://www.facebook.com/prokids.experience',
            enabled: true
          },
          twitter: {
            url: 'https://twitter.com/prokidsapp',
            enabled: false
          },
          instagram: {
            url: 'https://www.instagram.com/prokids.experience?igsh=MXFwOGx2azU3bWwxYg==',
            enabled: true
          },
          youtube: {
            url: 'https://www.youtube.com/@ProKidsExperience',
            enabled: true
          }
        },
        companyInfo: {
          name: 'Liga Prokids',
          description: 'Desarrollando jóvenes talentos',
          website: 'https://www.ligaprokid.es'
        },
        mapSettings: {
          latitude: 40.4168,
          longitude: -3.7038,
          zoom: 15
        }
      };
      
      setSettings(defaultSettings);
      return defaultSettings;
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener configuración completa (requiere autenticación)
  const fetchFullSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('prokids_token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`${API_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSettings(result.data);
          return result.data;
        } else {
          throw new Error('No se pudo obtener la configuración');
        }
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error al obtener configuración completa:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar configuración
  const updateSettings = async (newSettings) => {
    try {
      setError(null);

      const token = localStorage.getItem('prokids_token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`${API_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSettings(result.data);
          return { success: true, data: result.data };
        } else {
          throw new Error(result.message || 'Error al actualizar configuración');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error al actualizar configuración:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Función para obtener solo información de contacto formateada
  const getContactInfo = () => {
    if (!settings || !settings.contact) return null;

    return {
      address: settings.contact.address,
      phone: settings.contact.phone,
      email: settings.contact.email,
      schedule: settings.contact.schedule,
      fullAddress: `${settings.contact.address?.street}, ${settings.contact.address?.postalCode} ${settings.contact.address?.city}, ${settings.contact.address?.country}`
    };
  };

  // Función para obtener redes sociales activas
  const getActiveSocialMedia = () => {
    if (!settings || !settings.socialMedia) return [];

    const socialNetworks = [];

    Object.entries(settings.socialMedia).forEach(([platform, config]) => {
      // Solo verificar que esté habilitado y tenga URL válida (no vacía)
      if (config.enabled && config.url && config.url.trim() !== '') {
        // Mapeo de iconos para Material Icons
        let iconName = platform;
        switch (platform) {
          case 'twitter':
            iconName = 'alternate_email'; // Material Icons no tiene twitter, usamos 'alternate_email' para representar X
            break;
          case 'facebook':
            iconName = 'facebook'; // Material Icons tiene facebook
            break;
          case 'instagram':
            iconName = 'photo_camera'; // Material Icons no tiene instagram, usamos 'photo_camera'
            break;
          case 'youtube':
            iconName = 'smart_display'; // Material Icons usa 'smart_display' para YouTube
            break;
          default:
            iconName = platform;
        }

        socialNetworks.push({
          platform,
          url: config.url,
          icon: iconName
        });
      }
    });

    return socialNetworks;
  };

  // Función para obtener información de la empresa
  const getCompanyInfo = () => {
    if (!settings || !settings.companyInfo) return null;

    return settings.companyInfo;
  };

  // Función para obtener configuración del mapa
  const getMapSettings = () => {
    if (!settings || !settings.mapSettings) return null;

    return settings.mapSettings;
  };

  // Cargar configuración de contacto al montar el componente
  useEffect(() => {
    fetchContactInfo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    settings,
    loading,
    error,
    fetchContactInfo,
    fetchFullSettings,
    updateSettings,
    getContactInfo,
    getActiveSocialMedia,
    getCompanyInfo,
    getMapSettings,
    // Función de utilidad para refrescar configuración
    refresh: fetchContactInfo
  };
};

export default useSettings;
